<script setup lang="ts">
import { getDestinations } from '@/api/destinations/getDestinations';
import type { Destination } from '@/api/destinations/types';
import { urlFilters } from '@/composables/urlFilters';
import { LANGUAGE } from '@/env';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

const destinations = ref<Destination[]>([]);
const isLoading = ref(true);

const { filters, updateFilters } = urlFilters();
const { t } = useI18n();

onMounted(async () => {
    try {
        const dests = await getDestinations();
        destinations.value = dests.filter((d) => d.code !== 'Not Set');
    } catch (err) {
        console.error('Failed to load destinations:', err);
    } finally {
        isLoading.value = false;
    }
});

const selectedDestinations = computed({
    get(): Destination[] {
        if (!destinations.value.length) {
            return [];
        }

        return filters.destinations
            .map(code => destinations.value.find(dest => dest.code === code))
            .filter((dest): dest is Destination => dest !== undefined);
    },

    set(newSelection: Destination[]) {
        const newCodes = newSelection.map(dest => dest.code);
        updateFilters({ destinations: newCodes });
    }
});

function getDestinationName(destination: Destination): string {
    return destination.name[LANGUAGE] || destination.code;
}


</script>
<template>
    <Multiselect v-model="selectedDestinations" :options="destinations" :multiple="true" :loading="isLoading"
        track-by="code" :custom-label="getDestinationName" :select-label="t('MULTISELECT.selectLabel')"
        :selected-label="t('MULTISELECT.selectedLabel')" :deselect-label="t('MULTISELECT.deselectLabel')"
        :placeholder="$t('LABELS.anywhere')" :close-on-select="false">
        <template #noResult>
            {{ t('MULTISELECT.noResult') }}
        </template>
        <template #noOptions>
            {{ t('MULTISELECT.noOptions') }}
        </template>
    </Multiselect>
</template>
